import { useState, useCallback } from 'react';
import { safeEvaluatePattern } from '../utils/safePatternEvaluator';

const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * useChatHistory Hook
 *
 * Manages chat history state in-memory only (no persistence across reloads).
 * History will be available during the current page session and cleared on refresh.
 */
export const useChatHistory = () => {
  const [messages, setMessages] = useState([]);
  const [selectedMessageId, setSelectedMessageId] = useState(null);

  // Note: intentionally not persisted to localStorage so history doesn't survive page reloads


  // Add a user message
  const addUserMessage = useCallback((prompt) => {
    const message = {
      id: generateId(),
      type: 'user',
      timestamp: Date.now(),
      prompt
    };
    setMessages(prev => [...prev, message]);
    return message.id;
  }, []);

  // Add an assistant response (successful generation)
  const addAssistantMessage = useCallback((prompt, patternCode) => {
    const message = {
      id: generateId(),
      type: 'assistant',
      timestamp: Date.now(),
      prompt,
      patternCode
    };
    setMessages(prev => [...prev, message]);
    setSelectedMessageId(message.id);
    return message.id;
  }, []);

  // Add an error response
  const addErrorMessage = useCallback((prompt, errorMessage) => {
    const message = {
      id: generateId(),
      type: 'assistant',
      timestamp: Date.now(),
      prompt,
      error: errorMessage
    };
    setMessages(prev => [...prev, message]);
    return message.id;
  }, []);

  // Get pattern function from a message
  const getPatternFromMessage = useCallback((messageId) => {
    const message = messages.find(m => m.id === messageId);
    if (!message || message.type !== 'assistant' || !message.patternCode) {
      return null;
    }
    try {
      return safeEvaluatePattern(message.patternCode);
    } catch (err) {
      console.error('Failed to evaluate stored pattern:', err);
      return null;
    }
  }, [messages]);

  // Select a message
  const selectMessage = useCallback((messageId) => {
    setSelectedMessageId(messageId);
  }, []);

  // Get currently selected message
  const getSelectedMessage = useCallback(() => {
    return messages.find(m => m.id === selectedMessageId);
  }, [messages, selectedMessageId]);

  // Clear all history
  const clearHistory = useCallback(() => {
    setMessages([]);
    setSelectedMessageId(null);
  }, []);

  // Check if there's any history
  const hasHistory = messages.length > 0;

  // Get only assistant messages with valid patterns
  const getPatternMessages = useCallback(() => {
    return messages.filter(m => m.type === 'assistant' && m.patternCode && !m.error);
  }, [messages]);

  return {
    messages,
    selectedMessageId,
    hasHistory,
    addUserMessage,
    addAssistantMessage,
    addErrorMessage,
    getPatternFromMessage,
    selectMessage,
    getSelectedMessage,
    clearHistory,
    getPatternMessages
  };
};
